import React, { useEffect, useRef } from 'react';
import { trackEvent } from '../../utils/analytics';
import './HomeDemoFrame.css';

/**
 * HomeDemoFrame — the homepage fold's interactive product demo (round-6
 * revamp, Aug 2026). A coded, product-true CellCog workspace: 6 demo
 * employees, channels, DM scripts that end in a shift report, and working
 * Dashboard / Inbox / Tasks popups (with a clickable Approve).
 *
 * ARCHITECTURE — deliberately an imperative engine inside one React shell.
 * The demo is a scripted animation machine (timers, staged DOM appends,
 * hover-paused auto-advance) ported 1:1 from the verified mock
 * (Maeve's workspace work/home_round6/shared/demo.js — the locked functional
 * spec, iterated live with Nitish Aug 17-19). Rewriting it as declarative
 * React state would be a risky re-derivation of battle-tested behavior for
 * zero user benefit; instead the component owns a ref and boots the engine in
 * an effect. React never re-renders inside the frame.
 *
 * DESIGN RULINGS BAKED IN (do not re-litigate):
 *  - Option B avatars (Aug 19): 1:1 DM threads draw NO per-message avatars —
 *    the chat header names the person (iMessage pattern). Channels keep
 *    grouped avatars: multi-party, colour is functional there.
 *  - Consecutive-sender grouping: a sender's follow-ups don't redraw the
 *    avatar/name (repetition reads as noise in a small frame).
 *  - Motion until touched: the demo plays each employee's day ONCE through
 *    the roster, then holds. The first deliberate press kills the autopilot
 *    for good. Hover pauses the countdown. Hidden tab / out-of-viewport
 *    pauses. Touch devices and prefers-reduced-motion never auto-cycle.
 *  - Mobile (<=700px): the sidebar becomes the R2 rail (his pick) — 58px,
 *    avatar-only, tiny EMP label, hairline divider, ONE `# general` chip.
 *    Desktop keeps the full labelled sidebar with all three channels.
 *  - The Dashboard/Inbox/Tasks menu belongs to an EMPLOYEE, not a channel.
 *
 * Analytics (all real-visitor interactions; nothing programmatic fires):
 *  cta_click demo_touch (first press, once) · demo_employee_pick ·
 *  demo_channel_open · demo_popup_open — all compare_page:'home',
 *  location:'hero_demo'.
 *
 * QC deep-links (freeze mode, no timers):
 *  ?demo_freeze=1[&demo_role=slug|&demo_channel=slug][&demo_popup=dashboard|inbox|tasks][&demo_menu=1]
 *
 * Prerender: under the ReactSnap UA (and reduced-motion) the first scene
 * renders settled instantly — no timers, so the snapshot can never catch a
 * mid-animation frame (same doctrine as RoleRotator).
 */

/* ============ cast ============ */
const CAST = {
  nora: { name: 'Nora', role: 'Marketing Manager', color: '#7dc3c3', pron: 'her' },
  dean: { name: 'Dean', role: 'Sales Outreach', color: '#e8b45a', pron: 'his' },
  priya: { name: 'Priya', role: 'Research Assistant', color: '#a48ce0', pron: 'her' },
  grace: { name: 'Grace', role: 'Operations Manager', color: '#6db1f0', pron: 'her' },
  leo: { name: 'Leo', role: 'Software Engineer', color: '#7fd6a4', pron: 'his' },
  mia: { name: 'Mia', role: 'Designer', color: '#f096b8', pron: 'her' },
};

/* ============ DM scripts — natural conversations ending in a shift report ==
   convo item: [who, text, time, {att}?] · who='you'|'emp'
   Copy is LOCKED canon from the verified mock — every string traces to the
   round-6 decision record. Do not re-word without an explicit decision. */
const SCRIPTS = [
  {
    slug: 'marketing', who: 'nora',
    wake: '&#9200; Woke on schedule &middot; Mon 9:00 AM shift',
    convo: [
      ['emp', 'Morning! Overnight replies are handled: 12 answered, 2 flagged for you.', '9:02 AM'],
      ['emp', 'Today\u2019s 3 posts are staged. One question: OK to use the new tagline in Thursday\u2019s launch post?', '9:04 AM'],
      ['you', 'Approved. And add a line about the pricing update.', '9:14 AM'],
      ['emp', 'Done, updated and queued.', '9:15 AM'],
      ['you', 'How did last week\u2019s posts do?', '4:38 PM'],
      ['emp', 'Best week this month: 3 posts, 41 replies, signups up 18%. Full numbers are on my dashboard.', '4:41 PM'],
    ],
    report: { title: 'Shift report \u00b7 5:00 PM', lines: [
      ['em', '3 posts staged, tagline + pricing line in'],
      ['em', '12 overnight replies answered, 2 escalated'],
      ['pend', 'Waiting on you: approve Thursday\u2019s launch post'],
    ] },
    handover: 'Shift ended \u00b7 handover saved \u00b7 tomorrow\u2019s shift picks up right here',
    dash: { title: 'Campaign KPIs', stats: [['Signups this week', '132', '+18%'], ['Posts shipped', '9', 'on plan'], ['Replies handled', '27', '+6']],
      chart: { kind: 'line', label: 'Signups per week', x: ['12 wks ago', '', '', 'this week'],
        data: [38, 44, 41, 52, 49, 58, 55, 64, 61, 72, 68, 86] } },
    mail: [
      { from: 'Sam \u00b7 Growth letter', subj: '<b>Re: guest post swap</b> \u00b7 happy to cross-promote', badge: 'replied', when: '9:41 AM' },
      { from: 'press@techdaily', subj: '<b>Coverage request</b> \u00b7 can you send a product one-pager?', badge: 'replied', when: '9:12 AM' },
      { from: 'LinkedIn', subj: 'Your launch post passed 41 replies', when: '8:03 AM' },
    ],
    tasks: {
      wait: [{ t: 'Approve Thursday\u2019s launch post', n: 'Publishing waits for your yes', btn: true }],
      todo: [{ t: 'Draft the pricing-update announcement' }, { t: 'Refresh the campaign dashboard' }],
      done: [{ t: '12 overnight replies answered' }, { t: '3 posts staged for review' }],
    },
  },
  {
    slug: 'sales', who: 'dean',
    wake: '&#9993; Woke on signal &middot; a lead replied',
    convo: [
      ['emp', 'Jordan from Acme replied. They want a demo this week.', '2:11 PM'],
      ['emp', 'I proposed Thursday 2 PM and updated the pipeline in HubSpot. Calendar invite is drafted. Send it?', '2:12 PM'],
      ['you', 'Send it. And prep a one-pager for the call.', '2:20 PM'],
      ['emp', 'Both done. The one-pager is in your Drive.', '2:44 PM', { att: 'Acme_onepager.pdf' }],
      ['you', 'What about the two quiet leads from last week?', '3:05 PM'],
      ['emp', 'Follow-ups are drafted and scheduled for Thursday morning. They\u2019re on my board.', '3:06 PM'],
    ],
    report: { title: 'Shift report \u00b7 6:00 PM', lines: [
      ['em', 'Acme demo booked: Thu 2 PM, one-pager ready'],
      ['em', 'Pipeline updated, 2 follow-ups scheduled'],
      ['pend', 'Waiting on you: approve the 8-email outreach batch'],
    ] },
    handover: 'Shift ended \u00b7 handover saved \u00b7 next shift picks up right here',
    dash: { title: 'Pipeline', stats: [['Open conversations', '14', '+3'], ['Meetings booked', '4', 'this week'], ['Avg. reply time', '6 min', '&#8595; fast']],
      chart: { kind: 'funnel', label: 'Pipeline this month',
        data: [['Contacted', '120', 100], ['Replied', '38', 42], ['Meeting booked', '12', 22], ['Won', '4', 11]] } },
    mail: [
      { from: 'Jordan \u00b7 Acme Co', subj: '<b>Re: pricing question</b> \u00b7 thanks, that clears it up\u2026', badge: 'replied', when: '2:44 PM' },
      { from: 'Casey \u00b7 inbound', subj: '<b>Following up</b> \u00b7 can we talk Thursday instead?', badge: 'replied', when: '1:58 PM' },
      { from: 'Ridge Co', subj: '<b>New inbound</b> \u00b7 40 seats, evaluating this month', badge: 'flagged', when: '11:20 AM' },
    ],
    tasks: {
      wait: [{ t: 'Approve the 8-email outreach batch', n: 'External sends wait for your yes', btn: true }],
      todo: [{ t: 'Follow up with 2 quiet leads Thursday' }, { t: 'Research the new inbound from Ridge Co' }],
      done: [{ t: 'Acme demo booked: Thu 2 PM' }, { t: 'Pipeline updated in HubSpot' }],
    },
  },
  {
    slug: 'research', who: 'priya',
    wake: null,
    convo: [
      ['you', 'Compare the top 5 CRM tools for a 10-person team. Need it before my 2pm call.', '11:02 AM'],
      ['emp', 'On it. Reading pricing pages and reviews now.', '11:03 AM'],
      ['emp', 'Done: 5 tools, priced and ranked, top pick flagged with reasoning.', '11:49 AM', { att: 'CRM_Comparison.pdf' }],
      ['you', 'Perfect. Make a one-slide version for the call too.', '11:55 AM'],
      ['emp', 'Here it is: one slide, the ranking and the pick.', '12:06 PM', { att: 'CRM_oneslide.pdf' }],
      ['you', 'Great work. Add a weekly competitor digest to your routine.', '12:10 PM'],
    ],
    report: { title: 'Shift report \u00b7 12:30 PM', lines: [
      ['em', 'CRM comparison + one-slide delivered before the call'],
      ['em', 'Weekly competitor digest added to my routine, Fridays'],
      ['pend', 'Waiting on you: OK to buy the industry report?'],
    ] },
    handover: 'Shift ended \u00b7 handover saved \u00b7 Friday\u2019s digest is scheduled',
    /* Sources read per day (his call, Aug 18): the one number that IS her job.
       The 30 points carry real texture: weekday work, weekend dips, the spike
       on the CRM-comparison day. */
    dash: { title: 'Research delivered', stats: [['Reports this month', '11', '+4'], ['Sources read', '412', '&nbsp;'], ['Avg. per day', '13.7', '&#8593; 3.1']],
      chart: { kind: 'line', label: 'Sources read per day, last 30 days',
        x: ['30 days ago', '', '', 'today'],
        data: [9, 12, 14, 11, 15, 4, 3, 13, 16, 12, 18, 15, 5, 4, 14, 17, 21, 16, 19, 6, 5,
          15, 18, 24, 38, 22, 7, 6, 17, 20] } },
    mail: [
      { from: 'Statista library', subj: '<b>Your data request is ready</b> \u00b7 CRM market, 2024-2026', badge: 'replied', when: '11:44 AM' },
      { from: 'Author \u00b7 industry report', subj: '<b>Re: license question</b> \u00b7 single-seat is fine', badge: 'replied', when: '10:31 AM' },
      { from: 'Sector Digest', subj: 'Q3 market sizing \u00b7 summarized into your library', when: '7:15 AM' },
    ],
    tasks: {
      wait: [{ t: 'OK to buy the industry report?', n: 'Spending waits for your yes', btn: true }],
      todo: [{ t: 'Weekly competitor digest, Fridays' }, { t: 'Archive sources into the research library' }],
      done: [{ t: 'CRM comparison: 5 tools, ranked' }, { t: 'One-slide version for the 2pm call' }],
    },
  },
  {
    slug: 'operations', who: 'grace',
    wake: '&#9200; Woke on schedule &middot; weekly Monday operations check',
    convo: [
      ['emp', 'Weekly ops check done. Two vendor invoices are past due and one order came in short.', '8:07 AM'],
      ['emp', 'I chased both vendors and opened a claim on the short shipment. Drafts are ready to send.', '8:09 AM'],
      ['you', 'Send them. Where did we land on the new hire\u2019s onboarding?', '8:30 AM'],
      ['emp', 'Sent. Onboarding pack is assembled and in your Drive, first-day checklist included.', '8:41 AM', { att: 'onboarding_pack.pdf' }],
      ['you', 'Perfect. Keep the vendor check weekly.', '8:45 AM'],
    ],
    report: { title: 'Shift report \u00b7 4:00 PM', lines: [
      ['em', '2 vendor invoices chased, both answered'],
      ['em', 'Short shipment claim opened, onboarding pack ready'],
      ['pend', 'Waiting on you: approve the replacement order'],
    ] },
    handover: 'Shift ended \u00b7 handover saved \u00b7 next Monday\u2019s vendor check is scheduled',
    dash: { title: 'Operations', stats: [['Open items', '6', '&#8595; 4'], ['Invoices chased', '11', 'this month'], ['Avg. resolution', '1.4 days', '&#8595;']],
      chart: { kind: 'dbars', label: 'Days to resolve, weekly', goal: 'target 2 days', goalAt: 34,
        data: [86, 74, 68, 58, 54, 46, 42, 36, 32, 28, 24, 20] } },
    mail: [
      { from: 'Northwind Supply', subj: '<b>Re: invoice 4471</b> \u00b7 payment received, thanks', badge: 'replied', when: '8:41 AM' },
      { from: 'FastFreight', subj: '<b>Claim 8823</b> \u00b7 replacement ships Monday', badge: 'replied', when: '8:22 AM' },
      { from: 'Payroll', subj: '<b>New hire starts Monday</b> \u00b7 checklist needs your sign-off', badge: 'flagged', when: '7:50 AM' },
    ],
    tasks: {
      wait: [{ t: 'Approve the replacement order', n: 'Spending waits for your yes', btn: true }],
      todo: [{ t: 'Weekly vendor check, Mondays' }, { t: 'Refresh the supplier contact list' }],
      done: [{ t: '2 invoices chased, both answered' }, { t: 'Onboarding pack assembled' }],
    },
  },
  {
    slug: 'engineering', who: 'leo',
    wake: null,
    convo: [
      ['you', 'The signup form drops the referral code on mobile. Can you fix it today?', '10:15 AM'],
      ['emp', 'Reproduced it. Writing the fix plus a regression test now.', '10:22 AM'],
      ['emp', 'PR is open for your review: 12 lines, tested. Same bug class might exist on login; want me to check?', '11:03 AM'],
      ['you', 'Yes, check it.', '11:10 AM'],
      ['emp', 'Login checked. One edge case found and patched in the same PR.', '11:41 AM'],
      ['you', 'Nice catch. Ship it after review.', '11:45 AM'],
    ],
    report: { title: 'Shift report \u00b7 5:30 PM', lines: [
      ['em', 'Referral-code fix + login edge case: one PR, tested'],
      ['em', 'Task marked done on the board'],
      ['pend', 'Waiting on you: merge to production'],
    ] },
    handover: 'Shift ended \u00b7 handover saved \u00b7 next shift watches the deploy',
    dash: { title: 'Engineering board', stats: [['Tasks done this week', '7', '+2'], ['PRs awaiting you', '1', '&nbsp;'], ['Tests added', '19', '&nbsp;']],
      chart: { kind: 'dots', label: 'PRs shipped per shift', data: [1, 2, 1, 3, 2, 2, 4, 3, 2, 4, 3, 5] } },
    mail: [
      { from: 'GitHub', subj: '<b>PR 412</b> \u00b7 review requested from you', badge: 'flagged', when: '11:03 AM' },
      { from: 'CI \u00b7 build', subj: 'All checks passed on referral-code-fix', when: '10:58 AM' },
      { from: 'Error monitor', subj: 'Mobile signup errors back to zero since deploy', when: '9:30 AM' },
    ],
    tasks: {
      wait: [{ t: 'Merge the referral-code fix to production?', n: 'He ships to review alone. Production waits for you', btn: true }],
      todo: [{ t: 'Close out the mobile QA checklist' }, { t: 'Write up the bug class for the team' }],
      done: [{ t: 'Referral-code fix: PR open, tested' }, { t: 'Login edge case found and patched' }],
    },
  },
  {
    slug: 'design', who: 'mia',
    wake: null,
    convo: [
      ['you', 'Need launch visuals for Thursday: a banner and 3 social sizes, on brand.', '1:20 PM'],
      ['emp', 'Pulling your brand kit now. First versions within the hour.', '1:21 PM'],
      ['emp', 'Done. Banner plus LinkedIn, X and Instagram sizes are in your Drive.', '2:12 PM', { att: 'launch_visuals \u00b7 4 files' }],
      ['you', 'Love them. Can we get a dark-mode variant too?', '2:30 PM'],
      ['emp', 'Dark set is done: same 4 sizes, same kit.', '2:58 PM', { att: 'launch_visuals_dark \u00b7 4 files' }],
      ['you', 'Perfect, that\u2019s the launch set sorted.', '3:01 PM'],
    ],
    report: { title: 'Shift report \u00b7 5:00 PM', lines: [
      ['em', 'Launch set delivered: 8 files, light + dark'],
      ['em', 'All from your brand kit, no off-brand colors'],
      ['pend', 'Waiting on you: post the banner to the public site?'],
    ] },
    handover: 'Shift ended \u00b7 handover saved \u00b7 next brief picks up the same kit',
    /* Output per week, split by approved-as-is vs needed-a-revision (his call,
       Aug 18): rework shrinking over time is the quality story. */
    dash: { title: 'Studio output', stats: [['Assets this month', '23', '+8'], ['Awaiting your review', '4', '&nbsp;'], ['Approved as-is', '78%', '&#8593; 12']],
      chart: { kind: 'stack', label: 'Assets delivered per week',
        legend: [['on', 'Approved as-is'], ['rev', 'Needed a revision']],
        data: [[3, 3], [4, 2], [5, 2], [5, 1], [6, 1], [7, 0]] } },
    mail: [
      { from: 'Print vendor', subj: '<b>Re: banner specs</b> \u00b7 files look good to print', badge: 'replied', when: '2:58 PM' },
      { from: 'Figma', subj: '<b>3 comments</b> on Launch Banner v2', badge: 'flagged', when: '1:44 PM' },
      { from: 'Stock library', subj: 'License renewed for 12 months', when: '9:05 AM' },
    ],
    tasks: {
      wait: [{ t: 'Post the launch banner to the public site?', n: 'Anything public waits for your yes', btn: true }],
      todo: [{ t: 'Refresh the email header template' }, { t: 'Icon set for the new docs pages' }],
      done: [{ t: 'Launch banner + 3 social sizes' }, { t: 'Dark-mode set, 4 files' }],
    },
  },
];

/* ============ channels — real multi-employee rooms ============ */
const CHANNELS = [
  {
    slug: 'general', label: 'general', badge: null, desc: '6 members \u00b7 the whole team',
    convo: [
      ['grace', 'Weekly numbers are in: signups +18%, best week this month.', '9:03 AM'],
      ['nora', 'The launch post drove most of it. Thursday\u2019s follow-up is staged.', '9:05 AM'],
      ['leo', 'Deploy went clean overnight. Zero errors since.', '9:08 AM'],
      ['you', 'Great week, team.', '9:12 AM'],
    ],
  },
  {
    slug: 'marketing', label: 'marketing', badge: '2', desc: '3 members \u00b7 Nora, Grace, you',
    convo: [
      ['nora', 'Draft of Thursday\u2019s launch email is ready for review.', '10:14 AM', { att: 'launch_email_v2.md' }],
      ['grace', 'A/B on the landing headline: variant B is winning, +12% clicks. I\u2019d promote it.', '10:31 AM'],
      ['you', 'Nice, ship it.', '10:47 AM'],
      ['nora', 'Promoting variant B now. I\u2019ll fold it into Thursday\u2019s email too.', '10:48 AM'],
    ],
  },
  {
    slug: 'support', label: 'support', badge: null, desc: '3 members \u00b7 Leo, Priya, you',
    convo: [
      ['leo', 'The referral-code bug is fixed, deploying tonight.', '4:12 PM'],
      ['priya', 'I checked: 9 customers hit it this week. I\u2019ll email each once it\u2019s live.', '4:20 PM'],
      ['leo', 'Deployed. All clear on mobile.', '8:02 PM'],
      ['priya', 'All 9 customers notified, two already replied thanks.', '8:31 PM'],
    ],
  },
];

const FRAME_HTML = `
  <div class="frame">
    <div class="frame-chrome">
      <div class="fdots"><i></i><i></i><i></i></div>
      <div class="frame-title">Your workspace</div>
    </div>
    <div class="frame-body">
      <aside class="side">
        <div class="side-label side-label--first"><span class="lab-full">AI Employees</span><span class="lab-mini" aria-hidden="true">EMP</span></div>
        <div data-emps></div>
        <div class="side-label side-label--chan"><span class="lab-full">Channels</span></div>
        <div data-chans></div>
        <div class="side-me"><div class="av">You</div><div class="nm">You</div></div>
      </aside>
      <div class="chat">
        <div class="chat-head">
          <div class="av" data-chatav></div>
          <div class="who"><b data-chatname></b><span data-chatrole></span></div>
          <div class="head-tools">
            <div class="status-pill" data-statuspill>ON SHIFT</div>
            <button type="button" class="menu-btn" data-menubtn aria-label="Employee menu">&#8943;<span class="nbadge">1</span></button>
          </div>
          <div class="dmenu" data-menu>
            <button type="button" data-open-popup="dashboard">Dashboard</button>
            <button type="button" data-open-popup="inbox">Inbox</button>
            <button type="button" data-open-popup="tasks">Tasks<span class="mb">1</span></button>
          </div>
        </div>
        <div class="thread" data-thread></div>
        <div class="chat-input"><span data-inputph>Message Nora&hellip;</span><span class="mic" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M12 4.5v8"/><path d="M8.6 8.4v3.4a3.4 3.4 0 0 0 6.8 0V8.4"/><path d="M12 15.6v3.9"/><path d="M9.2 19.5h5.6"/></svg></span></div>
      </div>
    </div>

    <div class="doverlay" data-overlay>
      <div class="popup">
        <div class="popup-head">
          <div>
            <div class="ph-title" data-ptitle></div>
            <div class="ph-sub" data-psub></div>
          </div>
          <button type="button" class="pclose" data-popup-close aria-label="Close">&#10005;</button>
        </div>
        <div class="popup-body" data-pbody></div>
      </div>
    </div>
  </div>
  <div class="hint-bar" data-hint></div>
`;

const initials = (n) => n.slice(0, 1);

const isPrerender = () =>
  typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);

/* ============ per-role charts (one shape per role) ============ */
function renderChart(ch) {
  if (!ch) return '';
  const head = `<div class="cl">${ch.label}</div>`;
  if (ch.kind === 'line') {
    const max = Math.max(...ch.data);
    const n = ch.data.length;
    const pts = ch.data.map((v, i) => `${((i / (n - 1)) * 100).toFixed(1)},${(94 - (v / max) * 84).toFixed(1)}`);
    const last = pts[pts.length - 1].split(',');
    return `<div class="chartbox">${head}
      <svg class="spark" viewBox="0 0 100 100" preserveAspectRatio="none">
        <polygon class="sp-area" points="0,100 ${pts.join(' ')} 100,100"/>
        <polyline class="sp-line" points="${pts.join(' ')}" vector-effect="non-scaling-stroke"/>
      </svg>
      <svg class="sp-dot" viewBox="0 0 100 100" preserveAspectRatio="none"><circle cx="${last[0]}" cy="${last[1]}" r="1.6"/></svg>
      <div class="cx">${ch.x.map((x) => `<span>${x}</span>`).join('')}</div></div>`;
  }
  if (ch.kind === 'funnel') {
    return `<div class="chartbox">${head}<div class="funnel">${ch.data.map(([lab, v, pct]) =>
      `<div class="fn"><span class="fnl">${lab}</span><span class="fnb"><i style="width:${pct}%"></i></span><span class="fnv">${v}</span></div>`).join('')}</div></div>`;
  }
  if (ch.kind === 'dbars') {
    return `<div class="chartbox">${head}<div class="dwrap">
      <div class="dgoal" style="bottom:${ch.goalAt}%"><span>${ch.goal}</span></div>
      <div class="dbars dbars--down">${ch.data.map((h) => `<i style="height:${h}%"></i>`).join('')}</div></div></div>`;
  }
  if (ch.kind === 'dots') {
    const max = Math.max(...ch.data);
    return `<div class="chartbox">${head}<div class="lolli">${ch.data.map((v) =>
      `<span class="ls" style="height:${(v / max) * 100}%"><i></i></span>`).join('')}</div></div>`;
  }
  if (ch.kind === 'stack') {
    const tot = ch.data.map(([a, b]) => a + b);
    const max = Math.max(...tot);
    return `<div class="chartbox">${head}<div class="dstack">${ch.data.map(([a, b], i) =>
      `<span class="stk" style="height:${((a + b) / max) * 100}%" title="week ${i + 1}">
        <i class="on" style="flex:${a}"></i><i class="rev" style="flex:${b}"></i></span>`).join('')}</div>
      <div class="skey">${ch.legend.map(([c, t]) =>
    `<span class="sk"><i class="${c}"></i>${t}</span>`).join('')}</div></div>`;
  }
  return '';
}

/* ============ the engine ============ */
function bootDemo(host, page = 'home') {
  host.innerHTML = FRAME_HTML;
  const $ = (sel) => host.querySelector(sel);
  const $$ = (sel) => host.querySelectorAll(sel);

  const coarse = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const reduced = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const prerender = isPrerender();
  const press = coarse ? 'tap' : 'click';

  const hint = $('[data-hint]');
  hint.innerHTML = `Interactive demo &middot; illustrative example, not live data &middot; ${press} around`;

  let timers = [];
  let currentDM = 0;
  let view = { kind: 'dm', idx: 0 };
  let touched = false;
  const docListeners = [];
  let io = null;

  const onDoc = (type, fn) => { document.addEventListener(type, fn); docListeners.push([type, fn]); };
  const clearTimers = () => { timers.forEach((t) => clearTimeout(t)); timers = []; };
  const at = (ms, fn) => { timers.push(setTimeout(fn, ms)); };
  const scrollBottom = () => { const T = $('[data-thread]'); T.scrollTop = T.scrollHeight; };
  /* page attribution (Sep 9 2026): the frame also mounts on /ai-employees;
     each mount names its page, so the demo_* series stay separable. */
  const track = (name, extra) => trackEvent('cta_click', {
    cta_name: name, page, compare_page: page, location: 'hero_demo', ...extra,
  });

  function markSelection() {
    $$('.emp').forEach((c, j) => c.classList.toggle('sel', view.kind === 'dm' && j === view.idx));
    $$('.chan').forEach((c, j) => c.classList.toggle('sel', view.kind === 'chan' && j === view.idx));
  }

  function bubble(kind, whoKey, text, time, opts, cont) {
    const m = document.createElement('div');
    const isYou = kind === 'you';
    m.className = `msg ${isYou ? 'you' : 'emp-msg'}${cont ? ' cont' : ''}`;
    const c = isYou ? { name: 'You', color: '#3a4a52' } : CAST[whoKey];
    const av = `<div class="av" style="--emp:${c.color}">${isYou ? 'You' : initials(c.name)}</div>`;
    const ai = isYou ? '' : '<span class="aitag">AI</span>';
    const sender = `<div class="sender">${c.name}${ai}<span class="tstamp">${time || ''}</span></div>`;
    const att = (opts && opts.att) ? `<div class="att"><span class="fico">&#128196;</span>${opts.att}</div>` : '';
    m.innerHTML = `${av}<div class="bwrap">${sender}<div class="dbubble">${text}${att}</div></div>`;
    return m;
  }

  function reportBubble(whoKey, rep, cont) {
    const m = document.createElement('div');
    m.className = `msg emp-msg${cont ? ' cont' : ''}`;
    const c = CAST[whoKey];
    const lines = rep.lines.map((l) => `<li><span class="${l[0]}">${l[0] === 'em' ? '\u2713' : '\u25CB'}</span>${l[1]}</li>`).join('');
    m.innerHTML = `<div class="av" style="--emp:${c.color}">${initials(c.name)}</div>`
      + `<div class="bwrap"><div class="sender">${c.name}<span class="aitag">AI</span></div>`
      + `<div class="sreport"><div class="srh">${rep.title}</div><ul>${lines}</ul></div></div>`;
    return m;
  }

  /* ---- ambient auto-advance: motion until touched, then control ---- */
  const AUTO = {
    on: !prerender && !reduced, hold: 5200, timer: null, next: -1, left: 0, t0: 0,
    hovering: false, blocked: false, outview: false,
    still: coarse || reduced,
  };
  function autoStopHold() {
    if (AUTO.timer) { clearTimeout(AUTO.timer); AUTO.timer = null; }
    $$('.autobar').forEach((b) => b.remove());
  }
  function autoKill() {
    if (!touched) {
      touched = true;
      track('demo_touch');
    }
    if (!AUTO.on) return;
    AUTO.on = false; AUTO.next = -1;
    autoStopHold();
    hint.innerHTML = `Your turn &middot; ${press} any employee, channel, or the &#8943; menu &middot; illustrative example, not live data`;
  }
  function autoTick() {
    if (!AUTO.on || AUTO.next < 0 || AUTO.timer || AUTO.hovering || AUTO.blocked || AUTO.left <= 0) return;
    AUTO.t0 = Date.now();
    AUTO.timer = setTimeout(() => {
      AUTO.timer = null;
      const n = AUTO.next; AUTO.next = -1;
      if (AUTO.on && n >= 0 && n < SCRIPTS.length) playDM(n);
    }, AUTO.left);
    const row = $$('.emp')[view.idx];
    if (row) {
      let b = row.querySelector('.autobar');
      if (!b) { b = document.createElement('i'); b.className = 'autobar'; row.appendChild(b); }
      b.style.transition = `width ${AUTO.left}ms linear`;
      requestAnimationFrame(() => { b.style.width = '100%'; });
    }
  }
  function autoPauseHold() {
    if (!AUTO.timer) return;
    clearTimeout(AUTO.timer); AUTO.timer = null;
    AUTO.left = Math.max(0, AUTO.left - (Date.now() - AUTO.t0));
    const b = host.querySelector('.autobar');
    if (b) { const w = getComputedStyle(b).width; b.style.transition = 'none'; b.style.width = w; }
  }
  function autoAfterPlay(i, playEndMs) {
    if (!AUTO.on || AUTO.still || i + 1 >= SCRIPTS.length) return;
    at(playEndMs, () => {
      if (!AUTO.on) return;
      AUTO.next = i + 1; AUTO.left = AUTO.hold;
      autoTick();
    });
  }

  /* ---- DM view ---- */
  function setupDM(i, instant) {
    clearTimers(); autoStopHold();
    view = { kind: 'dm', idx: i }; currentDM = i; markSelection();
    const s = SCRIPTS[i];
    const c = CAST[s.who];
    const chatav = $('[data-chatav]');
    chatav.style.background = c.color; chatav.style.color = '#0a0b0d'; chatav.textContent = initials(c.name);
    $('[data-chatname]').textContent = c.name; $('[data-chatrole]').textContent = c.role;
    $('[data-inputph]').textContent = `Message ${c.name}\u2026`;
    $('[data-statuspill]').style.display = '';
    $('[data-menubtn]').style.display = '';
    const T = $('[data-thread]');
    T.innerHTML = ''; T.scrollTop = 0;
    /* Option B IS the design (his pick, Aug 19): DM threads draw no
       per-message avatars — the header already names the person. */
    T.classList.add('noav');
    const items = [];
    if (s.wake) {
      const w = document.createElement('div'); w.className = 'wake'; w.innerHTML = s.wake;
      items.push(w);
    }
    /* Consecutive messages from the same sender group: avatar + name draw once
       per run; follow-ups tuck underneath. The report card groups too when it
       follows the employee's own last message. */
    let prev = null;
    s.convo.forEach((m) => {
      items.push(bubble(m[0], s.who, m[1], m[2], m[3], m[0] === prev));
      prev = m[0];
    });
    items.push(reportBubble(s.who, s.report, prev === 'emp'));
    const h = document.createElement('div'); h.className = 'handover'; h.textContent = s.handover;
    items.push(h);

    if (instant) { items.forEach((el) => { el.classList.add('on'); T.appendChild(el); }); scrollBottom(); return; }
    /* Items are appended only WHEN they play (appending all up front gave the
       thread full scrollHeight while invisible, so the first scrollBottom()
       jumped past the conversation to a blank second screen). Plays ONCE and
       settles on the shift report + handover. */
    items.forEach((el, k) => at(150 + k * 950, () => {
      T.appendChild(el);
      requestAnimationFrame(() => el.classList.add('on'));
      if (T.scrollHeight > T.clientHeight) scrollBottom();
    }));
    autoAfterPlay(i, 150 + items.length * 950 + 900);
  }
  function playDM(i) { setupDM(i, false); }

  /* ---- channel view ---- */
  function setupChannel(i, instant) {
    clearTimers(); autoStopHold();
    view = { kind: 'chan', idx: i }; markSelection();
    const ch = CHANNELS[i];
    const chatav = $('[data-chatav]');
    chatav.style.background = 'rgba(125,195,195,.18)'; chatav.textContent = '#';
    chatav.style.color = '#7dc3c3';
    $('[data-chatname]').textContent = `#${ch.label}`; $('[data-chatrole]').textContent = ch.desc;
    $('[data-inputph]').textContent = `Message #${ch.label}\u2026`;
    $('[data-statuspill]').style.display = 'none';
    /* The Dashboard/Inbox/Tasks menu belongs to an EMPLOYEE, not a channel. */
    $('[data-menubtn]').style.display = 'none';
    closeMenu();
    const T = $('[data-thread]');
    T.innerHTML = ''; T.scrollTop = 0;
    T.classList.remove('noav'); /* channels keep avatars: colour is functional */
    const items = [];
    let prevWho = null;
    ch.convo.forEach((m) => {
      items.push(bubble(m[0] === 'you' ? 'you' : 'emp', m[0], m[1], m[2], m[3], m[0] === prevWho));
      prevWho = m[0];
    });
    if (instant) { items.forEach((el) => { el.classList.add('on'); T.appendChild(el); }); scrollBottom(); return; }
    items.forEach((el, k) => at(150 + k * 900, () => {
      T.appendChild(el);
      requestAnimationFrame(() => el.classList.add('on'));
      if (T.scrollHeight > T.clientHeight) scrollBottom();
    }));
  }
  function playChannel(i) { setupChannel(i, false); }

  /* ---- menu + popups ---- */
  function toggleMenu(e) { e.stopPropagation(); $('[data-menu]').classList.toggle('on'); }
  function closeMenu() { $('[data-menu]').classList.remove('on'); }

  function openPopup(kind) {
    closeMenu();
    const s = SCRIPTS[view.kind === 'dm' ? view.idx : currentDM];
    const c = CAST[s.who];
    const O = $('[data-overlay]'); const T = $('[data-ptitle]'); const U = $('[data-psub]'); const B = $('[data-pbody]');
    if (kind === 'dashboard') {
      T.textContent = `${c.name}\u2019s dashboard`;
      U.textContent = `${s.dash.title} \u00b7 ${c.pron === 'his' ? 'he keeps' : 'she keeps'} this fresh, you just look`;
      B.innerHTML = `<div class="pstats">${s.dash.stats.map((x) =>
        `<div class="pstat"><div class="sl">${x[0]}</div><div class="sv">${x[1]}</div><div class="sd">${x[2]}</div></div>`).join('')}</div>
      ${renderChart(s.dash.chart)}
      <div class="popup-foot">Every employee maintains live dashboards of their own work, no status meetings.</div>`;
    }
    if (kind === 'inbox') {
      T.textContent = `${c.name}\u2019s inbox`;
      U.textContent = `${c.pron === 'his' ? 'His' : 'Her'} own email address, handled without you`;
      B.innerHTML = s.mail.map((m) =>
        `<div class="mailrow"><div class="from">${m.from}</div><div class="subj">${m.subj}</div>`
        + (m.badge ? `<div class="${m.badge}">${m.badge}</div>` : '')
        + `<div class="when">${m.when}</div></div>`).join('')
        + '<div class="popup-foot" style="margin-top:14px">Every employee gets a real email address on day one. They answer what they can, and flag what needs you.</div>';
    }
    if (kind === 'tasks') {
      T.textContent = `${c.name}\u2019s tasks`;
      U.textContent = 'Waiting on you \u00b7 to-do \u00b7 done';
      const lane = (cls, label, count, rows) => `
      <div class="lane ${cls}"><div class="ln">${label}<span class="lc">${count}</span></div>${rows}</div>`;
      const waitRows = s.tasks.wait.map((t) =>
        `<div class="dtask wait-t"><div class="tx">${t.t}<div class="tnote">${t.n}</div></div>`
        + (t.btn ? '<button type="button" data-approve>Approve</button>' : '') + '</div>').join('');
      const todoRows = s.tasks.todo.map((t) => `<div class="dtask"><div class="tx">${t.t}</div></div>`).join('');
      const doneRows = s.tasks.done.map((t) => `<div class="dtask"><span class="dchk">&#10003;</span><div class="tx">${t.t}</div></div>`).join('');
      B.innerHTML =
        lane('wait', 'Waiting on you', s.tasks.wait.length, waitRows)
        + lane('todo', 'To-do', s.tasks.todo.length, todoRows)
        + lane('done', 'Done', s.tasks.done.length, doneRows)
        + '<div class="popup-foot">Nothing risky happens without your yes. You set the permission line per employee.</div>';
    }
    O.classList.add('on');
  }
  function closePopup() { $('[data-overlay]').classList.remove('on'); }

  /* ---- build the sidebar ---- */
  const empsEl = $('[data-emps]');
  SCRIPTS.forEach((s, i) => {
    const c = CAST[s.who];
    const e = document.createElement('div');
    e.className = `emp${i === 0 ? ' sel' : ''}`;
    e.setAttribute('role', 'button');
    e.setAttribute('tabindex', '0');
    e.setAttribute('aria-label', `${c.name}, ${c.role}`);
    e.innerHTML = `<div class="av" style="--emp:${c.color}">${initials(c.name)}</div><div class="emeta"><div class="nm">${c.name}</div><div class="rl">${c.role}</div></div><div class="onmark"></div>`;
    e.addEventListener('click', () => {
      closePopup(); closeMenu(); playDM(i);
      track('demo_employee_pick', { employee: c.name });
    });
    empsEl.appendChild(e);
  });
  const chansEl = $('[data-chans]');
  CHANNELS.forEach((ch, i) => {
    const e = document.createElement('div');
    e.className = 'chan';
    e.setAttribute('role', 'button');
    e.setAttribute('tabindex', '0');
    e.setAttribute('title', `#${ch.label}`);
    e.setAttribute('aria-label', `#${ch.label}`);
    e.innerHTML = `<span class="hash">#</span><span class="chan-name">${ch.label}</span>${ch.badge ? `<span class="cbadge">${ch.badge}</span>` : ''}`;
    e.addEventListener('click', () => {
      closePopup(); closeMenu(); playChannel(i);
      track('demo_channel_open', { channel: ch.label });
    });
    chansEl.appendChild(e);
  });

  /* ---- wiring ---- */
  $('[data-menubtn]').addEventListener('click', toggleMenu);
  $$('[data-open-popup]').forEach((b) => b.addEventListener('click', () => {
    const kind = b.getAttribute('data-open-popup');
    openPopup(kind);
    track('demo_popup_open', { popup: kind });
  }));
  $('[data-overlay]').addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closePopup();
  });
  $('[data-popup-close]').addEventListener('click', closePopup);
  /* Approve buttons are re-rendered per popup open — delegate. */
  $('[data-pbody]').addEventListener('click', (e) => {
    const btn = e.target.closest('[data-approve]');
    if (btn) btn.closest('.dtask').classList.add('approved');
  });
  const onDocClick = (e) => {
    if (!e.target.closest('[data-menu]') && !e.target.closest('[data-menubtn]')) closeMenu();
  };
  onDoc('click', onDocClick);
  const onKey = (e) => { if (e.key === 'Escape') { closePopup(); closeMenu(); } };
  onDoc('keydown', onKey);

  /* ---- QC freeze mode / boot ---- */
  const q = new URLSearchParams(window.location.search);
  if (q.get('demo_freeze') || prerender || reduced) {
    if (q.get('demo_channel')) {
      const ci = Math.max(0, CHANNELS.findIndex((x) => x.slug === q.get('demo_channel')));
      setupChannel(ci, true);
    } else {
      const idx = Math.max(0, SCRIPTS.findIndex((x) => x.slug === (q.get('demo_role') || 'marketing')));
      setupDM(idx, true);
    }
    if (q.get('demo_menu')) $('[data-menu]').classList.add('on');
    if (q.get('demo_popup')) openPopup(q.get('demo_popup'));
  } else {
    /* Motion until touched: the first deliberate press inside the demo ends
       the autopilot for good; hover pauses the countdown, leaving resumes. */
    host.addEventListener('pointerdown', autoKill, true);
    host.addEventListener('pointerenter', () => { AUTO.hovering = true; autoPauseHold(); });
    host.addEventListener('pointerleave', () => { AUTO.hovering = false; autoTick(); });
    const onVis = () => {
      AUTO.blocked = document.visibilityState !== 'visible' || AUTO.outview;
      if (AUTO.blocked) autoPauseHold(); else autoTick();
    };
    onDoc('visibilitychange', onVis);
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((en) => {
        AUTO.outview = !en[0].isIntersecting;
        AUTO.blocked = document.visibilityState !== 'visible' || AUTO.outview;
        if (AUTO.blocked) autoPauseHold(); else autoTick();
      }, { threshold: 0.25 });
      io.observe(host);
    }
    /* Robust autoplay: hidden tabs throttle timers — start when visible. */
    let started = false;
    const start = () => { if (!started) { started = true; playDM(0); } };
    if (document.visibilityState === 'visible') start();
    const onVisStart = () => { if (document.visibilityState === 'visible' && !started) start(); };
    onDoc('visibilitychange', onVisStart);
    const onPageShow = () => start();
    window.addEventListener('pageshow', onPageShow);
    docListeners.push(['__pageshow__', onPageShow]);
  }

  return {
    destroy() {
      clearTimers(); autoStopHold();
      if (io) io.disconnect();
      docListeners.forEach(([type, fn]) => {
        if (type === '__pageshow__') window.removeEventListener('pageshow', fn);
        else document.removeEventListener(type, fn);
      });
    },
  };
}

export default function HomeDemoFrame({ page = 'home' }) {
  const hostRef = useRef(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const engine = bootDemo(host, page);
    return () => engine.destroy();
  }, [page]);
  /* id is the demo link's scroll target from HomeFold. */
  return <div className="hdemo" id="home-demo" ref={hostRef} />;
}
